import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import RouteTelemetry from './components/RouteTelemetry';
import { AuthProvider } from './contexts/AuthContext';

// Container Management Components
import ContainerManagement from './components/ContainerManagement';
import ContainerManagementEnhanced from './components/ContainerManagementEnhanced';
import ContainerPlanning from './components/ContainerPlanning';
import ForkliftContainerApp from './components/ForkliftContainerApp';
import PackingListImport from './components/PackingListImport';
import ContainerReports from './components/ContainerReports';
import POReports from './components/POReports';
import AlertsDashboard from './components/AlertsDashboard';
import ImportHistory from './components/ImportHistory';
import ContainerExposureCalendar from './components/ContainerExposureCalendar';
import BatchTraceability from './components/BatchTraceability';

// WMS Shell + Routes
import WmsLayout from './components/wms/WmsLayout';
import { wmsRoutes } from './components/wms/wmsRoutes';

// Production Scheduling Shell + Routes
import ProductionShell from './components/production/ProductionShell';
import ProductionProvider from './components/production/ProductionProvider';
import { productionRoutes } from './components/production/productionRoutes';

// Prepress — lazy-loaded as a single unit. prepressRoutes.tsx statically imports GatePage, so a
// split "lazy shell, eager routes" import would still drag the gate (GatePage.tsx + gate.css, a
// real chunk) into the main bundle every container-ops/production/WMS user downloads. PrepressApp
// is the one dynamic-import boundary that keeps the whole /prepress/* subtree out until visited.
const PrepressApp = lazy(() => import('./components/prepress/PrepressApp'));

// Define Abaris brand colors
const abarisOrange = '#CB6015';
const abarisBackground = '#F8F8F8';

// Create a mobile-optimized theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: abarisOrange,
    },
    background: {
      default: abarisBackground,
    },
    success: {
      main: '#2E7D32',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#F57C00',
    },
  },
  typography: {
    // Larger font sizes for mobile readability
    h4: {
      fontSize: '1.8rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.4rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.2rem',
      fontWeight: 500,
    },
    button: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    // Mobile-optimized button sizes
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 48, // Minimum 48px for touch targets
          borderRadius: 8,
          textTransform: 'none',
        },
        sizeLarge: {
          minHeight: 56,
          fontSize: '1.1rem',
        },
      },
    },
    // Touch-friendly text fields
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '1.1rem',
            padding: '14px 16px',
          },
        },
      },
    },
    // Larger FAB for mobile
    MuiFab: {
      styleOverrides: {
        root: {
          width: 64,
          height: 64,
        },
      },
    },
    // Card styling for mobile
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    // Chip styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          {/* Inside Router so it can read the location; renders nothing. */}
          <RouteTelemetry />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Protected routes (non-production) */}
            <Route element={<ProtectedRoute />}>
              {/* Main Application Routes - Default to Container Management */}
              <Route path="/" element={<Navigate to="/containers" replace />} />

              {/* Container Management */}
              <Route path="/containers" element={<ContainerManagementEnhanced />} />
              <Route path="/containers/dashboard" element={<Navigate to="/containers" replace />} />
              <Route path="/containers/legacy" element={<ContainerManagement />} />
              <Route path="/containers/planning" element={<ContainerPlanning />} />
              <Route path="/containers/forklift" element={<ForkliftContainerApp />} />
              <Route path="/containers/import" element={<PackingListImport />} />
              <Route path="/containers/reports" element={<ContainerReports />} />
              <Route path="/containers/po-reports" element={<POReports />} />
              <Route path="/containers/import-history" element={<ImportHistory />} />
              <Route path="/containers/exposure" element={<ContainerExposureCalendar />} />
              <Route path="/containers/batch-trace" element={<BatchTraceability />} />

              {/* Alerts Management */}
              <Route path="/alerts" element={<AlertsDashboard />} />

              {/* WMS Routes — wrapped in WmsLayout (sidebar + title bar + error boundary) */}
              <Route element={<WmsLayout />}>
                {wmsRoutes.map((route) => (
                  <Route key={route.path} path={route.path} element={route.element} />
                ))}
              </Route>
            </Route>

            {/* Production — React Query provider wraps the authenticated shell
                so all production routes share the QueryClient cache. */}
            <Route element={<ProductionProvider />}>
              <Route element={<ProtectedRoute />}>
                <Route element={<ProductionShell />}>
                  {/* '/production' now lands on the Gantt board (the planning
                      solution); the old TodayView "Schedule" page was retired. */}
                  <Route path="/production" element={<Navigate to="/production/board" replace />} />
                  {productionRoutes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                  ))}
                </Route>
              </Route>
            </Route>

            {/* Prepress — own top-level block (third instance of the shell/routes-registry
                pattern, after WMS and Production), lazy-loaded as one unit so the gate's
                bundle (GatePage.tsx + gate.css) never reaches container-ops/production/WMS
                users who never visit it. */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/prepress/*"
                element={(
                  <Suspense fallback={null}>
                    <PrepressApp />
                  </Suspense>
                )}
              />
            </Route>

            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
